import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import MediaRender from '../../../components/MediaRender';
import Button from '../../../components/ui/Button';

const ProfileHeader = ({ user, isOwnProfile, onEditProfile, onAvatarUpload, onBioUpdate, onFriendAction, friendshipStatus }) => {
  const [isEditingBio, setIsEditingBio] = useState(false);
  const [bioText, setBioText] = useState(user?.bio || '');
  const fileInputRef = React.useRef(null);

  const handleBioSave = () => {
    setIsEditingBio(false);
    if (onBioUpdate) onBioUpdate(bioText);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file && onAvatarUpload) {
      onAvatarUpload(file);
    }
  };

  const handleBioCancel = () => {
    setBioText(user?.bio || '');
    setIsEditingBio(false);
  };

  const getFriendButtonContent = () => {
    switch (friendshipStatus) {
      case 'friends':
        return { text: 'Friends', icon: 'UserCheck', variant: 'secondary' };
      case 'pending':
        return { text: 'Request Sent', icon: 'Clock', variant: 'outline' };
      case 'received':
        return { text: 'Accept Request', icon: 'UserPlus', variant: 'default' };
      default:
        return { text: 'Add Friend', icon: 'UserPlus', variant: 'default' };
    }
  };

  const friendButton = getFriendButtonContent();

  return (
    <div className="bg-card rounded-lg shadow-elevation-1 overflow-hidden">
      {/* Cover Photo */}
      <div className="h-48 md:h-64 bg-gradient-to-r from-primary/20 to-secondary/20 relative">
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
      </div>
      {/* Profile Content */}
      <div className="px-4 md:px-6 pb-6">
        {/* Profile Photo */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between -mt-16 md:-mt-20">
          <div className="flex flex-col md:flex-row md:items-end md:space-x-6">
            <div className="relative mb-4 md:mb-0">
              <div className="w-32 h-32 md:w-40 md:h-40 rounded-full border-4 border-background overflow-hidden bg-surface">
                <MediaRender
                  type="image"
                  src={user?.profilePhoto}
                  alt={user?.profilePhotoAlt}
                  className="w-full h-full object-cover"
                />
              </div>
              {isOwnProfile && (
                <>
                  <input type="file" ref={fileInputRef} className="sr-only" accept="image/*" onChange={handleFileChange} />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="absolute bottom-2 right-2 w-10 h-10 bg-primary text-primary-foreground rounded-full flex items-center justify-center shadow-elevation-2 hover:bg-primary/90 transition-colors duration-150"
                    aria-label="Change profile photo"
                  >
                    <Icon name="Camera" size={18} />
                  </button>
                </>
              )}
            </div>

            {/* User Info */}
            <div className="flex-1 md:pb-4">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
                {user?.name}
              </h1>
              
              {/* Bio Section */}
              <div className="mb-4">
                {isEditingBio ? (
                  <div className="space-y-3">
                    <textarea
                      value={bioText}
                      onChange={(e) => setBioText(e?.target?.value)}
                      className="w-full p-3 border border-border rounded-lg resize-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      rows={3}
                      maxLength={150}
                      placeholder="Tell people about yourself..."
                    />
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">
                        {bioText?.length}/150 characters
                      </span>
                      <div className="flex space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleBioCancel}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="default"
                          size="sm"
                          onClick={handleBioSave}
                        >
                          Save
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="group">
                    <p className="text-muted-foreground text-sm md:text-base leading-relaxed">
                      {user?.bio || 'No bio available'}
                    </p>
                    {isOwnProfile && (
                      <button
                        onClick={() => setIsEditingBio(true)}
                        className="mt-2 text-primary text-sm hover:underline opacity-0 group-hover:opacity-100 transition-opacity duration-150"
                      >
                        Edit bio
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Stats */}
              <div className="flex items-center space-x-6 text-sm">
                <div className="flex items-center space-x-1">
                  <Icon name="Users" size={16} className="text-muted-foreground" />
                  <span className="font-medium text-foreground">{user?.friendsCount}</span>
                  <span className="text-muted-foreground">friends</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Icon name="MapPin" size={16} className="text-muted-foreground" />
                  <span className="text-muted-foreground">{user?.location}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Icon name="Calendar" size={16} className="text-muted-foreground" />
                  <span className="text-muted-foreground">Joined {user?.joinedDate}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center space-x-3 mt-4 md:mt-0 md:pb-4">
            {isOwnProfile ? (
              <Button
                variant="default"
                onClick={onEditProfile}
                iconName="Edit"
                iconPosition="left"
              >
                Edit Profile
              </Button>
            ) : (
              <>
                <Button
                  variant={friendButton?.variant}
                  onClick={() => onFriendAction(friendshipStatus)}
                  iconName={friendButton?.icon}
                  iconPosition="left"
                >
                  {friendButton?.text}
                </Button>
                <Button
                  variant="outline"
                  iconName="MessageCircle"
                  iconPosition="left"
                >
                  Message
                </Button>
              </>
            )}
            
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {/* Handle more options */}}
              aria-label="More options"
            >
              <Icon name="MoreHorizontal" size={18} />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileHeader;